import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// 1. Определяем более точные типы
type TranslationKey = string;
type TranslationValue = string | Record<string, any>;
type LanguageTranslations = Record<string, TranslationValue>;
type Translations = Record<string, LanguageTranslations>;

type TranslationContextType = {
  currentLanguage: string;
  setLanguage: (lang: string) => void;
  t: (key: string) => string;
  translations: Translations;
};

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

export const TranslationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [translations, setTranslations] = useState<Translations>({});

  useEffect(() => {
    const loadTranslations = async () => {
      try {
        const response = await fetch('/translations.json');
        const data: Translations = await response.json();
        setTranslations(data);
        
        const savedLang = localStorage.getItem('preferredLanguage');
        const browserLang = navigator.language.substring(0, 2).toLowerCase();

        if (savedLang && data[savedLang]) {
          setCurrentLanguage(savedLang);
        } else if (browserLang === 'ru' && data.ru) {
          setCurrentLanguage('ru');
        } else {
          setCurrentLanguage(data.en ? 'en' : Object.keys(data)[0] || 'en');
        }
      } catch (error) {
        console.error('Failed to load translations:', error);
      }
    };

    loadTranslations();
  }, []);

  const setLanguage = (lang: string) => {
    if (translations[lang]) {
      setCurrentLanguage(lang);
      localStorage.setItem('preferredLanguage', lang);
    }
  };

  // 2. Исправленная функция t с правильной типизацией
  const t = (key: string): string => {
    if (!translations[currentLanguage] && !translations.en) {
      return '';
    }

    const keys = key.split('.');
    
    // Начинаем с текущего языка
    let current: TranslationValue = translations[currentLanguage];
    
    // Ищем вложенные свойства
    for (const k of keys) {
      if (typeof current === 'object' && current !== null) {
        current = current[k];
      } else {
        current = undefined;
        break;
      }
    }
    
    // Если нашли строку - возвращаем
    if (typeof current === 'string') {
      return current;
    }
    
    // Fallback на английский
    if (currentLanguage !== 'en') {
      let enCurrent: TranslationValue = translations['en'];
      
      for (const k of keys) {
        if (typeof enCurrent === 'object' && enCurrent !== null) {
          enCurrent = enCurrent[k];
        } else {
          enCurrent = undefined;
          break;
        }
      }
      
      if (typeof enCurrent === 'string') {
        return enCurrent;
      }
    }
    
    // Если ничего не нашли - возвращаем ключ
    return key;
  };

  return (
    <TranslationContext.Provider value={{ currentLanguage, setLanguage, t, translations }}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};